import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close,
  Edit,
  Save,
  Send,
  Print,
  Description
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import DocumentHeader from '../documents/DocumentHeader';
import DocumentFooter from '../documents/DocumentFooter';
import LegalClausesSection from '../documents/LegalClausesSection';
import {
  renderMarkdown,
  SectionWrapper,
  SectionHeader,
  FieldLabel,
  DOCUMENT_COLORS,
  APPBAR_GRADIENT,
} from '../documents/documentStyles';

/**
 * BidDocumentPreview Component
 *
 * A professional, document-style preview of the bid.
 * Allows final review and edits before submission.
 */
const BidDocumentPreview = ({
  open,
  onClose,
  onSubmit,
  onSubmitToClient,
  data,
  contractor,
  onEditSection,
  loading,
  isEditMode = false
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  // Binding-quotation assent, required before sending to the client. Not shown
  // in edit mode (no submission happens there).
  const [assentAccepted, setAssentAccepted] = useState(false);
  const [assentError, setAssentError] = useState(false);
  const showAssent = !isEditMode && !!onSubmitToClient;
  const isAssessmentFirst = data?.bidIntent === 'assessment_request' || (parseFloat(data?.totalCost) || 0) <= 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartEdit = (section, value) => {
    setEditingSection(section);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    onEditSection(editingSection, editValue);
    setEditingSection(null);
    setEditValue('');
  };

  const validateForSubmit = () => {
    if (!isEditMode) {
      const errors = {};
      if ((data.executiveSummary || '').trim().length < 50) {
        errors.executiveSummary = 'Executive Summary must be at least 50 characters';
      }
      if ((data.scopeOfWork || '').trim().length < 50) {
        errors.scopeOfWork = 'Scope of Work must be at least 50 characters';
      }
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return false;
      }
    }
    setValidationErrors({});
    return true;
  };

  const handleSubmit = () => {
    if (validateForSubmit()) onSubmit();
  };

  const handleSubmitToClient = () => {
    if (showAssent && !assentAccepted) {
      setAssentError(true);
      return;
    }
    if (validateForSubmit() && onSubmitToClient) onSubmitToClient();
  };

  const isSubmittable =
    isEditMode ||
    ((data.executiveSummary || '').trim().length >= 50 &&
      (data.scopeOfWork || '').trim().length >= 50);

  const hasAttachments = data.attachments && data.attachments.length > 0;
  const sec = {
    executiveSummary: 1,
    scopeOfWork: 2,
    inclusionsExclusions: 3,
    supportingDocuments: hasAttachments ? 4 : null,
    investmentTimeline: hasAttachments ? 5 : 4,
    paymentTerms: hasAttachments ? 6 : 5,
    termsConditions: hasAttachments ? 7 : 6,
  };

  const renderEditableText = (section, value, multiline = false) => {
    if (editingSection === section) {
      return (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline={multiline}
            rows={multiline ? 4 : 1}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
            sx={{ mb: 1, bgcolor: 'background.paper' }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setEditingSection(null)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSaveEdit} startIcon={<Save />}>
              Save
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover .edit-icon': { opacity: 1 },
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={() => handleStartEdit(section, value)}
      >
        {multiline && value ? (
          renderMarkdown(value)
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {value || 'Click to add content...'}
          </Typography>
        )}
        <IconButton
          className="edit-icon"
          size="small"
          sx={{
            position: 'absolute', top: 0, right: 0,
            opacity: 0, transition: 'opacity 0.2s',
            bgcolor: 'background.paper'
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative', background: APPBAR_GRADIENT }} elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, fontWeight: 600 }} variant="h6" component="div">
            Quotation Preview
          </Typography>
          <Button color="inherit" onClick={() => window.print()} sx={{ mr: 1, textTransform: 'none' }}>
            <Print sx={{ mr: 1 }} /> Print
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleSubmit}
            disabled={loading || !isSubmittable}
            startIcon={loading ? null : <Save />}
            sx={{ mr: 1, textTransform: 'none', borderColor: 'rgba(255,255,255,0.6)' }}
          >
            {loading
              ? (isEditMode ? 'Updating...' : 'Saving...')
              : (isEditMode ? 'Save Changes' : 'Save as Draft')
            }
          </Button>
          {!isEditMode && onSubmitToClient && (
            <Button
              autoFocus
              variant="contained"
              onClick={handleSubmitToClient}
              disabled={loading || !isSubmittable}
              startIcon={loading ? null : <Send />}
              sx={{
                background: '#fff',
                color: DOCUMENT_COLORS.brand,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { background: '#F0F4FF' }
              }}
            >
              {loading ? 'Sending...' : 'Submit to Client'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4, bgcolor: DOCUMENT_COLORS.dialogBg, minHeight: '100vh' }}>
        {showAssent && (
          <Alert
            severity={assentError ? 'error' : 'info'}
            icon={false}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={assentAccepted}
                  onChange={(e) => { setAssentAccepted(e.target.checked); if (e.target.checked) setAssentError(false); }}
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Typography variant="body2">
                  I offer these terms as a binding quotation under the Eqaya standard terms, and my typed name
                  will be recorded as my signature.
                </Typography>
              }
            />
            {assentError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                Please confirm this before submitting to the client.
              </Typography>
            )}
          </Alert>
        )}
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 4, borderRadius: 2 }} id="bid-document">

          {/* Document Header */}
          <DocumentHeader
            documentType="QUOTATION"
            issuer={{
              name: contractor?.companyName || contractor?.name,
              logoUrl: contractor?.logo || contractor?.profile_image,
              city: contractor?.city || 'Zimbabwe',
            }}
            date={new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            projectTitle={data.projectType}
            meta={{ 'Valid Until': formatDate(data.validUntil) }}
          />

          <Divider sx={{ mb: 4 }} />

          {/* 1. Executive Summary */}
          <SectionWrapper>
            <SectionHeader
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              title={`${sec.executiveSummary}. Executive Summary`}
            />
            {validationErrors.executiveSummary && (
              <Alert severity="error" sx={{ mb: 1 }}>{validationErrors.executiveSummary}</Alert>
            )}
            <Box sx={{
              border: validationErrors.executiveSummary ? '2px solid #d32f2f' : 'none',
              borderRadius: 1,
              p: validationErrors.executiveSummary ? 1 : 0,
            }}>
              {renderEditableText('executiveSummary', data.executiveSummary, true)}
            </Box>
          </SectionWrapper>

          {/* 2. Scope of Work */}
          <SectionWrapper>
            <SectionHeader
              icon={<BuildOutlinedIcon fontSize="small" />}
              title={`${sec.scopeOfWork}. Scope of Work`}
            />
            {validationErrors.scopeOfWork && (
              <Alert severity="error" sx={{ mb: 1 }}>{validationErrors.scopeOfWork}</Alert>
            )}
            <Box sx={{
              border: validationErrors.scopeOfWork ? '2px solid #d32f2f' : 'none',
              borderRadius: 1,
              p: validationErrors.scopeOfWork ? 1 : 0,
            }}>
              {renderEditableText('scopeOfWork', data.scopeOfWork, true)}
            </Box>
          </SectionWrapper>

          {/* 3. Inclusions & Exclusions */}
          <SectionWrapper>
            <SectionHeader
              icon={<PlaylistAddCheckOutlinedIcon fontSize="small" />}
              title={`${sec.inclusionsExclusions}. Inclusions & Exclusions`}
            />
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.light' }}>
                  <Typography variant="subtitle2" color="success.main" fontWeight="bold" gutterBottom>
                    INCLUDED IN QUOTATION
                  </Typography>
                  <List dense disablePadding>
                    {data.inclusions.length > 0 ? data.inclusions.map((item, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.25 }}>
                        <ListItemText primary={`\u2022 ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    )) : <Typography variant="caption" color="text.secondary">No specific inclusions listed</Typography>}
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50', borderColor: 'error.light' }}>
                  <Typography variant="subtitle2" color="error.main" fontWeight="bold" gutterBottom>
                    EXCLUDED FROM QUOTATION
                  </Typography>
                  <List dense disablePadding>
                    {data.exclusions.length > 0 ? data.exclusions.map((item, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.25 }}>
                        <ListItemText primary={`\u2022 ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    )) : <Typography variant="caption" color="text.secondary">No specific exclusions listed</Typography>}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* 4. Supporting Documents (conditional) */}
          {hasAttachments && (
            <SectionWrapper>
              <SectionHeader
                icon={<FolderOutlinedIcon fontSize="small" />}
                title={`${sec.supportingDocuments}. Supporting Documents`}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.attachments.map((file, i) => (
                  <Chip key={i} label={file.name} icon={<Description fontSize="small" />} variant="outlined" size="small" />
                ))}
              </Box>
            </SectionWrapper>
          )}

          {/* Investment & Timeline */}
          <SectionWrapper>
            <SectionHeader
              icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
              title={`${sec.investmentTimeline}. ${isAssessmentFirst ? 'Assessment & Pricing Basis' : 'Investment & Timeline'}`}
            />
            {isAssessmentFirst && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This is an assessment-first quotation. It asks for the site information needed before the contractor can provide a final fixed price.
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: DOCUMENT_COLORS.brand }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Description</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(data.costBreakdown).map(([key, value]) => {
                        if (key === 'lineItems') return null;
                        const items = data.costBreakdown.lineItems?.[key] || [];
                        // Keep categories whose only content is at-purchase items
                        // (their priced total is 0 but the quantities matter).
                        if (!(value > 0) && items.length === 0) return null;
                        return (
                          <React.Fragment key={key}>
                            <TableRow>
                              <TableCell sx={{ textTransform: 'capitalize', fontWeight: items.length > 0 ? 600 : 400 }}>{key}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: items.length > 0 ? 600 : 400 }}>{formatCurrency(value)}</TableCell>
                            </TableRow>
                            {items.map((item, itemIndex) => (
                              <TableRow key={`${key}-${itemIndex}`}>
                                <TableCell sx={{ pl: 4, py: 0.5, color: 'text.secondary', fontSize: '0.85rem', borderBottom: 'none' }}>
                                  {item.description} — {item.quantity} {item.unit}{item.priceAtPurchase ? '' : ` × ${formatCurrency(item.unitPrice)}`}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5, color: 'text.secondary', fontSize: '0.85rem', borderBottom: 'none', fontStyle: item.priceAtPurchase ? 'italic' : 'normal' }}>
                                  {item.priceAtPurchase
                                    ? 'Priced at purchase'
                                    : formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0))}
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      <TableRow sx={{ bgcolor: DOCUMENT_COLORS.brandLight }}>
                        <TableCell><strong>{isAssessmentFirst ? 'FINAL PROJECT PRICE' : 'TOTAL PROJECT INVESTMENT'}</strong></TableCell>
                        <TableCell align="right"><strong>{isAssessmentFirst ? 'Pending assessment' : formatCurrency(data.totalCost)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {Object.keys(data.costBreakdown?.lineItems || {}).length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    The itemized quantities above are an estimate based on the site assessment. Actual quantities may be more or less, and additional items may be identified once work begins — any changes are agreed with you through change orders before extra cost is incurred.
                    {Object.values(data.costBreakdown.lineItems).flat().some((item) => item?.priceAtPurchase) &&
                      ' Items marked "priced at purchase" are quoted for quantity only because prices change frequently — the actual price is confirmed with you when the item is bought, and is not included in the total above.'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <FieldLabel>Project Timeline</FieldLabel>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Estimated Duration:</Typography>
                    <Typography variant="h6">
                      {data.estimatedDuration ? `${data.estimatedDuration} ${data.durationUnit}` : 'TBD after assessment'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Project Start Date:</Typography>
                    <Typography variant="h6">{formatDate(data.estimatedStartDate)}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* Payment Terms */}
          <SectionWrapper>
            <SectionHeader
              icon={<PaymentsOutlinedIcon fontSize="small" />}
              title={`${sec.paymentTerms}. Payment Terms`}
            />
            {isAssessmentFirst ? (
              <Alert severity="info">
                No mobilization deposit or secured construction balance is requested at this stage. Payment terms should be finalized after the site visit, measurements, and material selections are confirmed.
              </Alert>
            ) : (
            <Paper variant="outlined" sx={{ p: 3, borderLeft: '5px solid', borderColor: 'success.main' }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">Mobilization Deposit (Released Upfront)</Typography>
                    <Typography variant="body1" fontWeight="bold">{formatCurrency(data.depositAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Project Balance (Secure Holding)</Typography>
                    <Typography variant="body1" fontWeight="bold">{formatCurrency(data.totalCost - data.depositAmount)}</Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  {(data.paymentSchedule || []).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Payment Stages
                      </Typography>
                      {(data.paymentSchedule || []).map((stage, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                          <Typography variant="body2">
                            {index + 1}. {stage.name}{stage.description ? ` — ${stage.description}` : ''}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">{formatCurrency(stage.amount)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {(data.paymentSchedule || []).length > 1
                      ? 'Each stage is funded into secure escrow before work on it begins, and released to the contractor when that stage is completed and approved.'
                      : (data.paymentSchedule || []).length === 1
                        ? 'The project balance is held securely and released to the contractor immediately upon verified completion of the work.'
                        : 'After acceptance, the contractor and client agree a payment-stage breakdown. Each agreed stage is funded into secure escrow before work on it begins and released when it is completed and approved.'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {Math.round(((data.totalCost - data.depositAmount) / data.totalCost) * 100)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Secured Balance</Typography>
                </Grid>
              </Grid>
            </Paper>
            )}
          </SectionWrapper>

          {/* Terms & Conditions + Legal Clauses */}
          <LegalClausesSection
            documentType="bid"
            sectionNumber={sec.termsConditions}
            additionalTerms={data.termsAndConditions}
          />

          {/* Warranty & Validity */}
          <SectionWrapper>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <FieldLabel>Warranty Period</FieldLabel>
                <Typography variant="body2" fontWeight="bold">{data.warrantyPeriod}</Typography>
              </Box>
              <Box>
                <FieldLabel>Proposal Validity</FieldLabel>
                <Typography variant="body2" fontWeight="bold">{formatDate(data.validUntil)}</Typography>
              </Box>
            </Box>
          </SectionWrapper>

          <DocumentFooter documentType="quotation" />

        </Paper>
      </Container>
    </Dialog>
  );
};

export default BidDocumentPreview;
